import { useCallback } from 'react'
import { useLocation } from 'react-router-dom'

import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'

/** Which announcement variant the viewer saw. */
export type AnnouncementVariant = 'personalized' | 'fallback'

/** How the dialog was dismissed. */
export type DismissMethod = 'close_button' | 'cta'

/**
 * Tracking for the Insights launch announcement dialog. Mirrors
 * `usePublicGraphQLApiModalTracking` — reuses the generic `componentRendered`
 * event with a JSON `componentDetails` payload, so no tracking-plan changes are
 * needed.
 */
export function useInsightsAnnouncementTracking(): {
  trackDialogViewed: (variant: AnnouncementVariant) => boolean
  trackDialogDismissed: (
    method: DismissMethod,
    variant: AnnouncementVariant,
  ) => void
} {
  const commonTrackingProperties = useCommonTrackingProperties()
  const location = useLocation()

  const trackDialogViewed = useCallback(
    (variant: AnnouncementVariant): boolean => {
      if (!commonTrackingProperties.organizationId) return false

      BufferTracker.componentRendered({
        ...commonTrackingProperties,
        path: location.pathname,
        componentName: 'InsightsAnnouncementModal',
        componentDetails: JSON.stringify({ event: 'viewed', variant }),
      })
      return true
    },
    [commonTrackingProperties, location.pathname],
  )

  const trackDialogDismissed = useCallback(
    (method: DismissMethod, variant: AnnouncementVariant) => {
      if (!commonTrackingProperties.organizationId) return

      BufferTracker.componentRendered({
        ...commonTrackingProperties,
        path: location.pathname,
        componentName: 'InsightsAnnouncementModal',
        componentDetails: JSON.stringify({
          event: method === 'cta' ? 'cta_clicked' : 'dismissed',
          method,
          variant,
        }),
      })
    },
    [commonTrackingProperties, location.pathname],
  )

  return { trackDialogViewed, trackDialogDismissed }
}
